import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import PricingModal from '../Dashboard/PricingModal';
import {
    trackBuyCreditsClick,
    trackLogout,
    trackCreditsRefreshed,
    trackPricingModalOpened,
} from '../../utils/analytics';

const Header = () => {
    const navigate = useNavigate();
    const { user, logout, checkAuth } = useAuth();
    const [refreshing, setRefreshing] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [pricingOpen, setPricingOpen] = useState(false);

    const handleLogout = () => {
        trackLogout();
        logout();
        navigate('/login');
    };

    const handleBuyCredits = () => {
        trackBuyCreditsClick('header');
        trackPricingModalOpened('header');
        setPricingOpen(true);
        setMobileMenuOpen(false);
    };

    const handleRefreshCredits = async () => {
        setRefreshing(true);
        try {
            await checkAuth();
            trackCreditsRefreshed();
        } finally {
            setRefreshing(false);
        }
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    return (
        <>
            <style>{`
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .header-left {
          display: flex;
          align-items: center;
          cursor: pointer;
        }
        
        .header-right {
          display: flex;
          align-items: center;
          gap: 30px;
        }
        
        .header-credits {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .header-user {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .mobile-menu-btn {
          display: none;
          background: transparent;
          border: 1px solid var(--border-color);
          border-radius: 4px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 20px;
        }
        
        .mobile-menu {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: var(--card-bg);
          border-bottom: 1px solid var(--border-color);
          padding: 16px 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          z-index: 99;
        }
        
        .mobile-menu.open {
          display: block;
        }
        
        .mobile-menu-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid var(--border-color);
        }
        
        .mobile-menu-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        
        .mobile-menu-actions {
          display: flex;
          gap: 10px;
          margin-top: 16px;
        }
        
        .mobile-menu-actions button {
          flex: 1;
        }
        
        @media (max-width: 768px) {
          .header-right {
            display: none;
          }
          
          .mobile-menu-btn {
            display: block;
          }
          
          .header-logo-text {
            font-size: 18px !important;
          }
          
          .header-logo-image {
            height: 28px !important;
          }
        }
        
        @media (max-width: 480px) {
          .header-logo-text {
            display: none;
          }
        }
      `}</style>

            <header style={styles.header}>
                <div className="container header-container" style={{ position: 'relative' }}>
                    <div className="header-left" onClick={() => navigate('/dashboard')}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} className="header-logo-image" />
                        <h1 style={styles.logo} className="header-logo-text">
                            APILoadTest.com
                        </h1>
                    </div>

                    <div className="header-right">
                        <div className="header-credits">
                            <span style={styles.creditsLabel}>Credits:</span>
                            <span style={styles.creditsValue}>{user?.credits || 0}</span>
                            <button
                                className="btn btn-icon"
                                onClick={handleRefreshCredits}
                                disabled={refreshing}
                                style={styles.refreshBtn}
                                title="Refresh credits"
                            >
                                {refreshing ? '⟳' : '↻'}
                            </button>
                            <button
                                className="btn btn-gold btn-small"
                                onClick={handleBuyCredits}
                                style={{ marginLeft: '5px' }}
                            >
                                Buy More
                            </button>
                        </div>

                        <div className="header-user">
                            <span style={styles.email}>{user?.email}</span>
                            <button
                                className="btn btn-secondary btn-small"
                                onClick={handleLogout}
                            >
                                Logout
                            </button>
                        </div>
                    </div>

                    <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
                        {mobileMenuOpen ? '✕' : '☰'}
                    </button>

                    <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
                        <div className="mobile-menu-item">
                            <span style={styles.creditsLabel}>Credits</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={styles.creditsValue}>{user?.credits || 0}</span>
                                <button
                                    onClick={handleRefreshCredits}
                                    disabled={refreshing}
                                    style={styles.refreshBtn}
                                >
                                    {refreshing ? '⟳' : '↻'}
                                </button>
                            </div>
                        </div>
                        <div className="mobile-menu-item">
                            <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Account</span>
                            <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{user?.email}</span>
                        </div>
                        <div className="mobile-menu-actions">
                            <button
                                className="btn btn-gold btn-small"
                                onClick={handleBuyCredits}
                            >
                                Buy Credits
                            </button>
                            <button
                                className="btn btn-secondary btn-small"
                                onClick={handleLogout}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <PricingModal
                isOpen={pricingOpen}
                onClose={() => setPricingOpen(false)}
                onRefreshCredits={handleRefreshCredits}
            />
        </>
    );
};

const styles = {
    header: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'var(--card-bg)',
        borderBottom: '1px solid var(--border-color)',
        padding: '16px 0',
        zIndex: 100,
    },
    logo: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '26px',
        fontWeight: '700',
        fontStyle: 'normal',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        margin: 0,
    },
    logoImage: {
        height: '36px',
        width: 'auto',
        marginRight: '10px',
    },
    creditsLabel: {
        color: 'var(--text-secondary)',
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    creditsValue: {
        fontSize: '18px',
        fontWeight: '700',
        color: 'var(--text-primary)',
    },
    refreshBtn: {
        background: 'transparent',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        padding: '4px 8px',
        cursor: 'pointer',
        fontSize: '16px',
        color: 'var(--text-secondary)',
        transition: 'all 0.2s ease',
        minWidth: '32px',
    },
    email: {
        color: 'var(--text-secondary)',
        fontSize: '14px',
    },
};

export default Header;
